import { Injectable } from '@angular/core';
import { plural } from 'pluralize';
import { INote, IParentEntity } from '../interfaces/entity';
import { UserService } from './user.service';
import { User } from '../models/user';
import { Widget } from '../models/widget';
import { Process } from '../models/process';
import { Task } from '../models/task';
import { Marker } from '../models/marker';
import { Referral } from '../models/referral';
import { enumReviewStatus, Review } from '../models/review';
import { Assessment } from '../models/assessment';
import { enumAttachmentType } from './attachment.service';
import {
  FacReportObject,
  FtsuReportObject,
  IOnlineReportObject,
  RafReportObject,
} from '../models/report-objects';
import { DateHelper } from 'app/main/content/misc/date.module';

// Helper service used to construct HTML fragments used for printouts
// Abstracted into dedicated service due to potential circular dependencies
@Injectable()
export class PrintHtmlService {
  _color: string;
  _backgroundColor: string;
  _borderColor: string;

  constructor(
    private userService: UserService,
    private dateHelper: DateHelper
  ) {}

  get color(): string {
    return this._color;
  }

  set color(val: string) {
    this._color = val;
  }

  get backgroundColor() {
    return this._backgroundColor;
  }

  set backgroundColor(val: string) {
    this._backgroundColor = val;
  }

  get borderColor() {
    return this._borderColor;
  }

  set borderColor(val: string) {
    this._borderColor = val;
  }

  get sectionBreak() {
    return '<div style="padding-bottom: 30px;"></div><div style="border-top: 1px solid; padding-bottom: 10px;"></div>';
  }

  get divider() {
    return '<hr align="left" style="width: 40%; min-width: 500px;">';
  }

  get cardStyle(): string {
    return `<style>
                    .card {
                        width: 100%;                  /* Set width of cards */
                        display: flex;                /* Children use Flexbox */
                        flex-direction: column;       /* Rotate Axis */
                        border: 1px solid ${
                          this._borderColor || 'rgba(0, 0, 0, 0.12)'
                        };    /* Set up Border */
                        border-radius: 4px;           /* Slightly Curve edges */
                        overflow: hidden;             /* Fixes the corners */
                        margin: 20px 0px;                  /* Add space between cards */
                        break-inside: avoid;         /* avoid page break in a card if possible */
                    }

                    .card-header {
                        color: ${this._color || '#FFFFFF'};
                        text-align: left;
                        font-size: 20px;
                        font-weight: 600;
                        border-bottom: 1px solid ${
                          this._borderColor || 'rgba(0, 0, 0, 0.12)'
                        };
                        background-color: ${this._backgroundColor || '#0277bd'};
                        padding: 5px 10px;
                    }

                    .card-main {
                        display: flex;              /* Children use Flexbox */
                        flex-direction: column;     /* Rotate Axis to Vertical */
                        /* justify-content: center; */   /* Group Children in Center */
                        /* align-items: center; */       /* Group Children in Center (on cross axis) */
                        padding: 15px 10px;            /* Add padding to the top/bottom */
                    }

                    .card-row {
                        display: flex;
                        flex-direction: row;
                        padding: 5px;            /* Add padding to the top/bottom */
                    }

                    @media print{
                        .card{
                            width: 100%;
                        }
                    }

                    .info{
                        display: flex;
                        flex-direction: column;
                        flex: 1 0 auto;
                        min-height: 48px;
                    }

                    .row{
                        display: flex;
                        flex-direction: row;
                        justify-content: space-between;
                        align-items: center;
                    }

                    .list-row{
                        display: flex;
                        flex-direction: row;
                        justify-content: start;
                        align-items: center;
                    }

                    .column{
                        display: flex;
                        flex-direction: column;
                        flex: 1 0 auto;
                        min-height: 10px;
                    }

                    .status{
                        width: 100px;
                        min-width: 10%;
                        padding: 10px;
                        text-align: center;
                    }

                    .bg-red{
                        background-color: red;
                        color: white !important;
                    }

                    .bg-green{
                        background-color: green;
                        color: white !important;
                    }

                    .bg-orange, .bg-amber{
                        background-color: orange;
                    }

                    .bg-default{
                        background-color: ${this._backgroundColor || '#0277bd'};
                        color: ${this._color || '#FFFFFF'} !important;
                    }

                    .fieldValue{
                        // color: #039be5;
                    }

                    .label{
                        font-weight: bold;
                    }

                </style>`;
  }

  getSummary(parent: IParentEntity, includeSummaryInHeader?: boolean): string {
    return includeSummaryInHeader ? ` for ${parent.summary}` : '';
  }

  getDateTimeStamp(date: string | number | Date, seperator?: string): string {
    seperator = seperator || ' at ';
    if (date) {
      return (
        this.dateHelper.getDateAsString(date, 'DD/MMM/YYYY') +
        seperator +
        this.dateHelper.getDateAsString(date, 'HH:mm')
      );
    } else {
      return '';
    }
  }

  getHeader(title: string): string {
    return `<h1>${title}</h1>`;
  }

  createCard(header: string, content: string): string {
    const html = `<div class="card">
                        <div class="card-header">${header}</div>
                        <div class="card-main">${content}</div>
                    </div>`;
    return html;
  }

  printedBy(includeDateAndTime?: boolean): string {
    const currentUser = new User(
      this.userService.getUserById(this.userService.getCurrentUserId())
    );
    const printedBy = currentUser ? `Printed by ${currentUser.fullName}` : '';
    const timestamp = includeDateAndTime
      ? ' on ' + this.getDateTimeStamp(Date.now())
      : '';
    return `<div style="padding-top:10px;">${printedBy}${timestamp}</div>`;
  }

  getNotes(
    parent: IParentEntity,
    notes: INote[],
    includeSummaryInHeader?: boolean
  ): string {
    // add parent as header
    const noteType = parent.subjectType
      ? parent.subjectType + ' Notes'
      : 'Notes';
    let html = '';
    if (notes.length === 0) {
      html += `<div>There are no notes</div>`;
    }
    notes.forEach((i) => {
      const seq = i.sequenceId || 'Draft';
      const timestamp = this.getDateTimeStamp(i.note.dateCreated);
      const spacer =
        '<span style="border-right:1px solid; padding-left:5px;"></span><span style="padding-right:5px;"></span>';
      const userName =
        i.note.user && i.note.user.fullName
          ? i.note.user.fullName
          : 'Unspecified User';
      html +=
        '<div class="column" style="padding: 10px 0px 0px 0px; margin-bottom:-5px;">';
      html += `<div><strong>${seq}${spacer}${userName}${spacer}${timestamp}</strong>`;
      if (i.note.starred) {
        html += `${spacer}Starred`;
      }
      if (i.note.important) {
        html += `${spacer}Important`;
      }
      if (i.note.sensitive) {
        html += `${spacer}Sensitive`;
      }
      if (i.note.noteType && i.note.noteType.name.length > 0) {
        html += `${spacer}${i.note.noteType.name}`;
      }
      html += `</div><hr style="width: 100%"><div style="padding:10px 0px 40px 0px;">${i.note.detail}</div></div>`;
    });
    const length = notes.length > 0 ? `(${notes.length})` : '';
    const header = `${noteType}${this.getSummary(
      parent,
      includeSummaryInHeader
    )} ${length}`;
    return this.createCard(header, html);
  }

  getLinkedSubjects(
    parent: IParentEntity,
    linkedSubjects: any[],
    includeSummaryInHeader?: boolean
  ): string {
    let html = '';
    if (linkedSubjects.length === 0) {
      html += `<div>There are no linked subjects</div>`;
    }
    linkedSubjects.forEach((i, idx) => {
      if (idx > 0) {
        html += '<hr style="width: 100%">';
      }

      if (i.relationship.toLowerCase() == 'main target') {
        html += `<div class="info">
        <div class="row">
        <div class="column">
        <div><strong>${i.summary}*</strong></div>
        <div>${i.relationship}</div>
        </div>
        <div>${i.type}</div>
        </div>
     </div>`;
      } else {
        html += `<div class="info">
          <div class="row">
          <div class="column">
                        <div><strong>${i.summary}</strong></div>
                        <div>${i.relationship}</div>
                        </div>
                        <div>${i.type}</div>
                        </div>
                        </div>`;
      }
    });
    const length =
      linkedSubjects.length > 0 ? `(${linkedSubjects.length})` : '';
    const header = `Linked Subjects${this.getSummary(
      parent,
      includeSummaryInHeader
    )} ${length}`;
    return this.createCard(header, html);
  }

  getProcesses(
    parent: IParentEntity,
    processTypeName: string,
    processes: Process[],
    includeSummaryInHeader?: boolean
  ): string {
    let html = '';
    if (processes.length === 0) {
      html += `<div>There are no ${plural(processTypeName)}</div>`;
    }
    processes.forEach((i, idx) => {
      if (idx > 0) {
        html += '<hr style="width: 100%">';
      }
      // if no summary defined in Process Type then use ProcessType name. To change, change in ProcessType Summary
      if (!i.summary)
        i.summary=processTypeName;
      html += `<div>${i.summary}</div>`;
    });
    const length = processes.length > 0 ? `(${processes.length})` : '';
    const header = `${plural(processTypeName)}${this.getSummary(
      parent,
      includeSummaryInHeader
    )} ${length}`;
    return this.createCard(header, html);
  }

  getTasks(
    parent: IParentEntity,
    tasks: Task[],
    includeSummaryInHeader?: boolean
  ): string {
    let html = '';
    if (tasks.length === 0) {
      html += `<div>There are no tasks</div>`;
    } else {
      tasks.forEach((i, idx) => {
        let relevantDate = '';
        const locked = i.taskStatus > 20;
        if (i.dateDue && !locked) {
          relevantDate = `Due on ${this.dateHelper.getDateAsString(i.dateDue)}`;
        }
        if (i.taskStatus === 30) {
          relevantDate = `Completed on ${this.dateHelper.getDateAsString(
            i.dateCompleted
          )}`;
        }
        if (i.taskStatus === 40) {
          relevantDate = `Abandoned on ${this.dateHelper.getDateAsString(
            i.dateCompleted
          )}`;
        }
        let taskTypeName = '';
        if (i.taskType) {
          taskTypeName = i.taskType.name || 'Unknown Status';
          if (i.taskStatus < 30) {
            taskTypeName += '/' + i.taskStatusName;
          }
        } else {
          taskTypeName = 'Unknown Task Type ' + i.taskTypeId;
        }

        let flags = '';
        if (i.starred && i.important) {
          flags += `Starred | Important`;
        } else {
          if (i.starred) {
            flags += `Starred`;
          }
          if (i.important) {
            flags += 'Important';
          }
        }

        i.summary = i.summary ? i.summary : i.taskType.name;

        if (idx > 0) {
          html += '<hr style="width: 100%">';
        }

        html += `<div class="info">

                            <div class="row">
                                <div class="title">
                                    <span class="text-truncate"><strong>${i.summary}</strong></span>
                                </div>
                                <div>${relevantDate}</div>
                            </div>

                            <div class="row">
                                <div>${taskTypeName}</div>
                                <div>${flags}</div>
                            </div>
                        </div>`;
      });
    }
    const length = tasks.length > 0 ? `(${tasks.length})` : '';
    const header = `Tasks${this.getSummary(
      parent,
      includeSummaryInHeader
    )} ${length}`;
    return this.createCard(header, html);
  }

  getAttachments(
    parent: IParentEntity,
    attachments: [],
    includeSummaryInHeader?: boolean
  ): string {
    let html = '';
    attachments = attachments || [];
    if (attachments.length === 0) {
      html += `<div>There are no attachments</div>`;
    }
    attachments.forEach((i, idx) => {
      if (idx > 0) {
        html += '<hr style="width: 100%">';
      }
      const attachment = i as any;

      if (attachment.attachmentType === enumAttachmentType.File) {
        html += `<div style="display:flex;flex-direction:row;">
                        <div class="material-icons" style="padding-right:12px;min-width:24px;max-width:24px;"><mat-icon>description</mat-icon></div>
                        <div class="row" style="width: 100%">
                            <div>${attachment.fileName}</div>
                            <div>${this.getDateTimeStamp(
                              attachment.lastModifiedDate,
                              ' '
                            )}</div>
                        </div></div>`;

        if (attachment.summary && attachment.summary.length > 0) {
          html += `<div style="display:flex;flex-direction:row;">
                            <div class="row" style="width: 100%; padding-left:36px;">
                                <div>${attachment.summary}</div>
                            </div></div>`;
        }
      }
      if (attachment.attachmentType === enumAttachmentType.Url) {
        html += `<div style="display:flex;flex-direction:row;">
                            <div class="material-icons" style="padding-right:12px;max-width:24px;"><mat-icon>link</mat-icon></div>
                            <div class="row" style="width: 100%">
                                <div class="text-truncate">${
                                  attachment.url.link
                                }</div>
                                <div>${this.getDateTimeStamp(
                                  attachment.url.dateAdded,
                                  ' '
                                )}</div>
                            </div>
                        </div>
                        <div style="display:flex;flex-direction:row;" >
                            <div class="row" style = "width: 100%; padding-left:36px;" >
                                <div>${attachment.url.summary} </div>
                            </div>
                        </div>`;
      }
    });
    const length = attachments.length > 0 ? `(${attachments.length})` : '';
    const header = `Attachments${this.getSummary(
      parent,
      includeSummaryInHeader
    )} ${length}`;
    return this.createCard(header, html);
  }

  getMarkers(
    parent: IParentEntity,
    markers: Marker[],
    includeSummaryInHeader?: boolean
  ): string {
    let html = '';
    if (markers.length === 0) {
      html += `<div>There are no markers</div>`;
    }
    markers.forEach((i, idx) => {
      if (idx > 0) {
        html += '<hr style="width: 100%">';
      }

      html += `<div class="info">
                        <div class="list-row">
                            <div style="width: 48px;">
                            <div class="material-icons" style="color:${i.color};"><mat-icon>room</mat-icon></div>
                            </div>
                            <div>
                                <div class="row">
                                    <div class="text-truncate"><strong>${i.name}</strong></div>
                                </div>
                                <div class="row text-truncate">
                                    <div class="secondary-text">${i.description ||''}</div>
                                </div>
                            </div>
                        </div>
                     </div>`;
    });
    const length = markers.length > 0 ? `(${markers.length})` : '';
    const header = `Markers${this.getSummary(
      parent,
      includeSummaryInHeader
    )} ${length}`;
    return this.createCard(header, html);
  }

  getLinkedMarkers(
    parent: IParentEntity,
    linkedMarkers: any[],
    includeSummaryInHeader?: boolean
  ): string {
    let html = '';
    if (linkedMarkers.length === 0) {
      html += `<div>There are no linked markers</div>`;
    }
    linkedMarkers.forEach((i, idx) => {
      if (idx > 0) {
        html += '<hr style="width: 100%">';
      }

      html += `<div class="info">
                        <div class="list-row">
                            <div style="width: 48px;">
                            <div class="material-icons" style="color:${i.marker.color};"><mat-icon>room</mat-icon></div>
                            </div>
                            <div>
                                <div class="row">
                                    <div class="text-truncate"><strong>${i.marker.name}</strong></div>
                                </div>
                                <div class="row text-truncate">
                                    <div class="secondary-text">${i.subject.summary}</div>
                                </div>
                            </div>
                        </div>
                     </div>`;
    });
    const length = linkedMarkers.length > 0 ? `(${linkedMarkers.length})` : '';
    const header = `Linked Markers${this.getSummary(
      parent,
      includeSummaryInHeader
    )} ${length}`;
    return this.createCard(header, html);
  }

  getReferrals(parent: IParentEntity, referrals: Referral[]): string {
    if (referrals.length === 0) {
      return this.createCard(`Referrals`, `<div>There are no referrals</div>`);
    }
    let result = '';
    referrals.forEach((i, idx) => {
      let html = '';
      var referralDetails: string = '';
      var rpt: IOnlineReportObject = null;
      if (i.referralSource && i.reportJson) {
        var data: any = JSON.parse(i.reportJson);
        if (i.referralSource.name == 'File a Concern Website')
          rpt = new FacReportObject(data);
        if (i.referralSource.name == 'ReportAFraud Website')
          rpt = new RafReportObject(data);
        if (i.referralSource.name == 'Freedom to Speak Up Website')
          rpt = new FtsuReportObject(data);

        if (rpt)
          referralDetails =
            '<div style="width: 850px;">' + rpt.getHTML() + '</div>';
      }

      const referralSourceName = i.referralSource ? i.referralSource.name : '';

      html += `<div style="padding: 12px;" fxLayout="column">
                        <div class="row">
                            <div style="width: 425px;">
                                <table style="width: 100%">
                                    <tr><td class="label">Source:</td><td class="fieldValue">${
                                      referralSourceName || ''
                                    }</td></tr>
                                    <tr><td class="label">Summary:</td><td class="fieldValue">${
                                      i.summary || ''
                                    }</td></tr>
                                    <tr><td class="label">Referrer:</td><td class="fieldValue">${
                                      i.referrerName || ''
                                    }</td></tr>
                                    <tr><td class="label">Referrer Telephone:</td><td class="fieldValue">${
                                      i.referrerTelephone || ''
                                    }</td></tr>
                                    <tr><td class="label">Referrer Email:</td><td class="fieldValue">${
                                      i.referrerEmail || ''
                                    }</td></tr>
                                </table>
                            </div>
                            <div style="width: 425px;">
                                <table style="width: 100%">
                                    <tr><td></td><td></td></tr>
                                    <tr><td class="label">Date:</td><td class="fieldValue">${
                                      this.dateHelper.getDateAsString(
                                        i.referralDate
                                      ) || ''
                                    }</td></tr>
                                    <tr><td class="label">${
                                      i.managerName && i.managerName.length > 0
                                        ? 'Manager:'
                                        : ''
                                    }</td><td class="fieldValue">${
        i.managerName || ''
      }</td></tr>
                                    <tr><td class="label">${
                                      i.managerTelephone &&
                                      i.managerTelephone.length > 0
                                        ? 'Manager Telephone:'
                                        : ''
                                    }</td><td class="fieldValue">${
        i.managerTelephone || ''
      }</td></tr>
                                    <tr><td class="label">${
                                      i.managerEmail &&
                                      i.managerEmail.length > 0
                                        ? 'Manager Email:'
                                        : ''
                                    }</td><td class="fieldValue">${
        i.managerEmail || ''
      }</td></tr>
                                </table>
                            </div>
                        </div>
                        <br/>
                        <div class="row">
                            ${referralDetails}
                        </div>
                    </div>`;

      result += this.createCard(`Referral - ${i.summary}`, html);
    });
    return result;
  }

  getReviews(
    parent: IParentEntity,
    reviews: Review[],
    includeSummaryInHeader?: boolean
  ): string {
    let html = '';
    if (reviews.length === 0) {
      html += `<div>There are no reviews</div>`;
    }
    reviews.forEach((i, idx) => {
      if (idx > 0) {
        html += '<hr style="width: 100%">';
      }

      html += `<div class="info">
                        <div class="list-row">
                            <div class="status ${this.getStatusClass(
                              i.status
                            )}">${enumReviewStatus[i.status]}</div>
                            <div class="row" style="padding-left: 12px">
                                <div fxLayout="column">
                                    <div class="h3">${this.dateHelper.getDateAsString(
                                      i.reviewDate
                                    )}${i.label ? ` - ${i.label}` : ``} ${
        i.override ? '(override)' : ''
      }</div>
                                    <div>
                                        <span class="secondary-text">Reviewed by: ${
                                          i.reviewedBy.fullName
                                        }</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                     </div>`;
    });
    const length = reviews.length > 0 ? `(${reviews.length})` : '';
    const header = `Reviews${this.getSummary(
      parent,
      includeSummaryInHeader
    )} ${length}`;
    return this.createCard(header, html);
  }

  getAssessments(
    parent: IParentEntity,
    assessments: Assessment[],
    includeSummaryInHeader?: boolean
  ): string {
    let html = '';
    if (assessments.length === 0) {
      html += `<div>There are no assessments</div>`;
    }
    assessments.forEach((i, idx) => {
      if (idx > 0) {
        html += '<hr style="width: 100%">';
      }

      const templateName = i.assessmentTemplate
        ? i.assessmentTemplate.name
        : '';
      const reviewStatus =
        i.review && i.review.status
          ? i.review.status
          : i.assessmentTemplate && i.assessmentTemplate.autoReview
          ? enumReviewStatus.Pending
          : enumReviewStatus.None;

      html += `<div class="info">
                        <div class="list-row">
                            <div class="status ${this.getStatusClass(
                              reviewStatus
                            )}">${
        i.score
      } / ${i.acceptanceThreshold.toString()}</div>
                            <div class="row" style="padding-left: 12px; padding-right: 12px; width: 90%;">
                                <div fxLayout="column">
                                    <div class="h3">${
                                      i.completed
                                        ? this.dateHelper.getDateAsString(
                                            i.completedDate
                                          ) +
                                          ' - ' +
                                          templateName
                                        : templateName +
                                          '<span style="color: #039be5;"> - In Progress</span>'
                                    }</div>
                                    <div>${
                                      i.completedBy
                                        ? 'Completed by: ' +
                                          i.completedBy.fullName
                                        : ''
                                    }</div>
                                </div>
                                <div>${this.getStatusText(reviewStatus)}</div>
                            </div>
                        </div>
                     </div>`;
    });
    const length = assessments.length > 0 ? `(${assessments.length})` : '';
    const header = `Assessments${this.getSummary(
      parent,
      includeSummaryInHeader
    )} ${length}`;
    return this.createCard(header, html);
  }

  getSubjectWidgets(
    parent: IParentEntity,
    widgets: Widget[],
    maxCols?: number
  ): string {
    let html = '<div class="card-row">';
    let multiplier = 4;
    if (maxCols > 25) {
      multiplier = 3;
    }
    if (maxCols > 33) {
      multiplier = 2;
    }
    let row = 0;
    let column = 0;
    widgets.forEach((widget) => {
      const isLabel = widget.type.toLowerCase() === 'label';
      if (widget.config.y > row) {
        html += '</div><div class="card-row">';
        row = widget.config.y;
        column = 0;
        if (widget.config.x > 0) {
          html += this.getWidgetDiv(widget.config.x * multiplier);
          column = widget.config.x;
        }
      }
      if (widget.config.x > column) {
        html += this.getWidgetDiv((widget.config.x - column) * multiplier);
      }
      switch (widget.type.toLowerCase()) {
        case 'linewidgetcomponent':
          html += this.getLineWidgetDiv(widget.config.cols * multiplier);
          break;
        default:
          var textValue=widget.metadata.value;
          if (widget.metadata.type=='yesno')
          {
            if (textValue=='true')
              textValue='Yes';
            else if (textValue=='false')
              textValue='No';
            else 
              textValue='-';
          }
          html += this.getWidgetDiv(
            widget.config.cols * multiplier,
            (isLabel ? '<strong>' : '') +
            textValue +
              (isLabel ? '</strong>' : '')
          );
          break;
      }
      column = widget.config.x + widget.config.cols;
    });
    html += '</div>';
    const header =
      (parent.subjectType ? parent.subjectType : 'Subject') +
      (parent.summary ? ` - ${parent.summary}` : '');
    return this.createCard(header, html);
  }

  getWidgetDiv(width: number, content?: string) {
    return `<div style="width:${width}% !important;">${content || ''}</div>`;
  }

  getLineWidgetDiv(width: number) {
    return (
      `<div style="width:${width}% !important;display: flex;align-items: center;height: 100%;padding: 4px;">` +
      '<hr style="width: 100%;border-style: solid;"></hr></div>'
    );
  }

  private getStatusClass(status) {
    switch (status) {
      case enumReviewStatus.Accepted:
        return 'bg-green';
      case enumReviewStatus.Rejected:
        return 'bg-red';
      case enumReviewStatus.Pending:
        return 'bg-orange';
      default:
        return 'bg-default';
    }
  }

  private getStatusText(status) {
    switch (status) {
      case enumReviewStatus.Accepted:
        return '(Accepted)';
      case enumReviewStatus.Rejected:
        return '(Rejected)';
      default:
        return '';
    }
  }
}
